import { type Dispatch, type SetStateAction } from "react";

import { Alert, AlertTitle, Button, Stack, Typography } from "@mui/material";
import capitalize from "lodash/capitalize";

import { useApiContext } from "../../../../../api/context";
import { useCustomerId } from "../../../../../Components/hooks/useCustomerId";
import { useErrorSnackbar, useSuccessSnackbar } from "../../../../../Components/SharedSnackbar/SharedSnackbar.context";
import useSegmentTrackEvent from "../../../../../utils/useSegmentTrackEvent";
import { CONNECTION_TEST_EVENT, createConnectionTestEventProperties, DATADOG_FEATURE } from "../../segmenTracking";
import { type FormState, type ProductOption } from "../types";

type Props = {
  formState: FormState;
  onChange: Dispatch<SetStateAction<FormState>>;
  clearSubscriptions: () => void;
};

type Error = { response: { data: { error: string } } };

export default function ValidationStep({ formState, onChange, clearSubscriptions }: Props) {
  const customerId = useCustomerId();
  const api = useApiContext();
  const showErrorSnackbar = useErrorSnackbar();
  const showSuccessSnackbar = useSuccessSnackbar(1);
  const { trackEvent } = useSegmentTrackEvent();

  const testConnection = async () => {
    let connectionTestResult = false;
    onChange((state) => ({ ...state, loading: true }));
    const { applicationKey, APIKey, orgIdRegion } = formState;
    const productOptions: ProductOption[] = [];
    let connectionError = "";

    const trackConnectionTest = (result: "success" | "failed", extraProps: Record<string, unknown> = {}) => {
      trackEvent(CONNECTION_TEST_EVENT, {
        ...createConnectionTestEventProperties(DATADOG_FEATURE),
        source: "ValidationStep",
        connectionTestResult: result,
        ...extraProps,
      });
    };

    try {
      const response: { data: ProductOption[] } = await api.post(
        `/v1/customers/${customerId}/integrations/datadog/fetch-products`,
        {
          applicationKey,
          APIKey,
          orgIdRegion,
        }
      );

      productOptions.push(...response.data);
      connectionTestResult = true;
      showSuccessSnackbar("Success");
      trackConnectionTest("success");
    } catch (error) {
      const errorMessage = capitalize((error as Error)?.response?.data?.error);
      const defaultErrorMessage = "Error testing connection. Check the data and try again.";
      connectionError = errorMessage || defaultErrorMessage;
      showErrorSnackbar(connectionError);
      trackConnectionTest("failed", { error: errorMessage });
    }
    onChange((state) => ({ ...state, connectionTestResult, connectionError, productOptions, loading: false }));
    clearSubscriptions();
  };

  return (
    <Stack data-cy="validation-step" spacing={4}>
      <Typography variant="h3">Test your Datadog connection with DoiT</Typography>

      <Stack spacing={2}>
        <Typography variant="body1">
          To make sure the setup is successful, we need to test the connection. This should take a couple of seconds.
        </Typography>
        <Button
          variant="outlined"
          sx={{ width: "140px" }}
          onClick={testConnection}
          disabled={formState.connectionTestResult !== null}
          loading={formState.loading}
        >
          Test connection
        </Button>
        {formState.connectionTestResult === false && (
          <Alert severity="error">
            <Stack>
              <AlertTitle>Test failed</AlertTitle>
              {formState.connectionError}
            </Stack>
          </Alert>
        )}
        {formState.connectionTestResult && <Alert severity="success">Test successful</Alert>}
      </Stack>
    </Stack>
  );
}
